import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './ManagementPages.css';

const BANK_ACCOUNTS = {
  meezan: {
    accountName: 'Muhammad Hamza Alvi',
    accountNumber: '11490105300391',
    bankName: 'Meezan Bank',
    bankAddress: 'Meezan Bank- ASKARI XI BR LHR',
    iban: 'PK96MEZN0011490105300391',
    swiftCode: 'MEZNPKKA'
  },
  community: {
    accountName: 'Muhammad Hamza Alvi',
    accountNumber: '8313670573',
    bankName: 'Community Federal Savings Bank',
    bankAddress: '89-16 Jamaica Ave, Woodhaven, NY, 11421, United States',
    iban: '',
    swiftCode: 'CMFGUS33',
    routingNumber: '26073150',
    accountType: 'Checking'
  },
  elevate: {
    accountName: 'Muhammad Hamza Alvi',
    beneficiaryAddress: '89-A Sector A, Street 25, Askari 11, Lahore, 54000, Pakistan',
    accountNumber: '900100881645',
    bankName: 'Merchants Commercial Bank',
    bankAddress: '',
    iban: '',
    swiftCode: '',
    routingNumber: '021606742',
    accountType: 'Checking'
  }
};

const formatStatus = (status) => {
  if (!status) return '';
  return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedBank, setSelectedBank] = useState('meezan');
  const [filterMonth, setFilterMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    project: '',
    from: {
      name: 'Muhammad Hamza Alvi',
      title: 'Hubspot Consultant',
      address: '89-A Sector A, Street 25, Askari 11, Lahore, Pakistan 54000',
      phone: '+92-3334330550',
      cnic: '35201-3954106-1'
    },
    to: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    items: [{ date: new Date().toISOString().split('T')[0], description: '', amount: 0 }],
    currency: 'USD',
    tax: 0,
    status: 'draft',
    bankDetails: {
      accountName: 'Muhammad Hamza Alvi',
      accountNumber: '11490105300391',
      bankName: 'Meezan Bank',
      bankAddress: 'Meezan Bank- ASKARI XI BR LHR',
      iban: 'PK96MEZN0011490105300391',
      swiftCode: 'MEZNPKKA'
    },
    notes: '',
    useLetterhead: false
  });

  useEffect(() => {
    fetchInvoices();
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMonth, filterYear]);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      const response = await api.get('/api/invoices', { params });
      setInvoices(response.data);
    } catch (err) {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/api/projects');
      setProjects(response.data);
    } catch (err) {
      console.error('Failed to load projects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvoice) {
        await api.put(`/api/invoices/${editingInvoice._id}`, formData);
      } else {
        await api.post('/api/invoices', formData);
      }
      fetchInvoices();
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save invoice');
    }
  };

  const handleMarkPaid = async (invoice) => {
    if (!window.confirm(`Mark invoice ${invoice.invoiceNumber} as paid?`)) return;
    try {
      await api.put(`/api/invoices/${invoice._id}`, { ...invoice, status: 'paid', paidDate: new Date().toISOString().split('T')[0] });
      fetchInvoices();
    } catch (err) {
      setError('Failed to mark invoice as paid');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await api.delete(`/api/invoices/${id}`);
        fetchInvoices();
      } catch (err) {
        setError('Failed to delete invoice');
      }
    }
  };

  const downloadPDF = async (id, invoiceNumber) => {
    try {
      const response = await api.get(`/api/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleClone = async (invoice) => {
    // Prompt user for new invoice number
    const newInvoiceNumber = window.prompt('Enter invoice number for the cloned invoice:');

    // If user cancels, return
    if (!newInvoiceNumber || newInvoiceNumber.trim() === '') {
      return;
    }

    // Check if invoice number already exists
    const exists = invoices.some(inv => inv.invoiceNumber === newInvoiceNumber);
    if (exists) {
      setError(`Invoice number "${newInvoiceNumber}" already exists. Please use a different number.`);
      return;
    }

    // Create cloned invoice data with transformations
    const clonedData = {
      invoiceNumber: newInvoiceNumber.trim(),
      project: invoice.project?._id || '',
      from: { ...invoice.from },
      to: invoice.to,
      invoiceDate: new Date().toISOString().split('T')[0], // Current date
      dueDate: '', // Clear due date
      items: invoice.items.map(item => ({
        date: item.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        description: item.description,
        amount: item.amount
      })),
      currency: invoice.currency,
      tax: invoice.tax || 0,
      status: 'draft', // Reset to draft
      bankDetails: invoice.bankDetails ? { ...invoice.bankDetails } : { ...BANK_ACCOUNTS.meezan },
      notes: invoice.notes || '',
      useLetterhead: invoice.useLetterhead || false
    };

    // Open modal with cloned data
    setEditingInvoice(null); // Not editing, creating new
    setSelectedBank(detectBankFromDetails(invoice.bankDetails));
    setFormData(clonedData);
    setShowModal(true);
  };

  const openModal = (invoice = null) => {
    if (invoice) {
      setEditingInvoice(invoice);
      setSelectedBank(detectBankFromDetails(invoice.bankDetails));
      setFormData({
        ...invoice,
        project: invoice.project?._id || '',
        invoiceDate: invoice.invoiceDate?.split('T')[0],
        dueDate: invoice.dueDate?.split('T')[0] || '',
        items: invoice.items.map(item => ({
          ...item,
          date: item.date?.split('T')[0] || new Date().toISOString().split('T')[0]
        })),
        bankDetails: invoice.bankDetails || { ...BANK_ACCOUNTS.meezan }
      });
    } else {
      setSelectedBank('meezan');
      setEditingInvoice(null);
      setFormData({
        invoiceNumber: '',
        project: '',
        from: {
          name: 'Muhammad Hamza Alvi',
          title: 'Hubspot Consultant',
          address: '89-A Sector A, Street 25, Askari 11, Lahore, Pakistan 54000',
          phone: '+92-3334330550',
          cnic: '35201-3954106-1'
        },
        to: '',
        invoiceDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        items: [{ date: new Date().toISOString().split('T')[0], description: '', amount: 0 }],
        currency: 'USD',
        tax: 0,
        status: 'draft',
        bankDetails: {
          accountName: 'Muhammad Hamza Alvi',
          accountNumber: '11490105300391',
          bankName: 'Meezan Bank',
          bankAddress: 'Meezan Bank- ASKARI XI BR LHR',
          iban: 'PK96MEZN0011490105300391',
          swiftCode: 'MEZNPKKA'
        },
        notes: '',
        useLetterhead: false
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingInvoice(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('from.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, from: { ...prev.from, [field]: value } }));
    } else if (name.startsWith('bank.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, bankDetails: { ...(prev.bankDetails || {}), [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const detectBankFromDetails = (bankDetails) => {
    if (bankDetails?.bankName?.includes('Merchants')) {
      return 'elevate';
    }
    if (bankDetails?.routingNumber || bankDetails?.bankName?.includes('Community')) {
      return 'community';
    }
    return 'meezan';
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { date: new Date().toISOString().split('T')[0], description: '', amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleBankSelect = (bankKey) => {
    setSelectedBank(bankKey);
    setFormData(prev => ({
      ...prev,
      bankDetails: { ...BANK_ACCOUNTS[bankKey] }
    }));
  };

  const calculateTotal = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    const tax = parseFloat(formData.tax || 0);
    return (subtotal + tax).toFixed(2);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Invoice Management</h1>
        <button className="btn btn-primary" onClick={() => openModal()}>+ Add Invoice</button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}>
          {MONTHS.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <input
          type="number"
          value={filterYear}
          onChange={(e) => setFilterYear(parseInt(e.target.value))}
          min="2020"
          max="2099"
          style={{ width: '90px' }}
        />
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '20px' }}>
          {Object.entries(
            invoices.reduce((acc, inv) => {
              const cur = inv.currency || 'USD';
              acc[cur] = (acc[cur] || 0) + (inv.total || 0);
              return acc;
            }, {})
          ).map(([currency, total]) => (
            <div key={currency} style={{ fontSize: '13px', color: '#aaa' }}>
              Total: <span style={{ fontWeight: 'bold', color: '#fff', fontSize: '15px' }}>{currency} {total.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>To</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => (
              <tr key={invoice._id}>
                <td>{invoice.invoiceNumber}</td>
                <td>{typeof invoice.to === 'string' ? invoice.to.split('\n')[0] : invoice.to?.name || ''}</td>
                <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                <td>{invoice.currency} {invoice.total?.toFixed(2)}</td>
                <td>
                  <span className={`status-badge status-${invoice.status}`}>{formatStatus(invoice.status)}</span>
                </td>
                <td>
                  <button className="btn-icon" onClick={() => downloadPDF(invoice._id, invoice.invoiceNumber)} title="Download PDF">📥</button>
                  <button className="btn-icon" onClick={() => handleClone(invoice)} title="Clone Invoice">📋</button>
                  <button className="btn-icon btn-edit" onClick={() => openModal(invoice)} title="Edit">✏️</button>
                  {invoice.status !== 'paid' && (
                    <button className="btn-icon" onClick={() => handleMarkPaid(invoice)} title="Mark as Paid" style={{ color: '#27ae60' }}>✓</button>
                  )}
                  <button className="btn-icon btn-delete" onClick={() => handleDelete(invoice._id)} title="Delete">🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '800px'}}>
            <div className="modal-header">
              <h2>{editingInvoice ? 'Edit Invoice' : 'Create Invoice'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="useLetterhead"
                    checked={formData.useLetterhead || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, useLetterhead: e.target.checked }))}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Use Letterhead
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Invoice Number *</label>
                  <input name="invoiceNumber" value={formData.invoiceNumber} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Project</label>
                  <select name="project" value={formData.project} onChange={handleChange}>
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Invoice Date *</label>
                  <input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" name="dueDate" value={formData.dueDate} onChange={handleChange} />
                </div>
              </div>

              <h3>From (Your Details)</h3>
              <div className="form-group">
                <label>Name *</label>
                <input name="from.name" value={formData.from.name} onChange={handleChange} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Title</label>
                  <input name="from.title" value={formData.from.title} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="from.phone" value={formData.from.phone} onChange={handleChange} />
                </div>
              </div>

              <h3>To (Client Details)</h3>
              <div className="form-group">
                <label>Client Details *</label>
                <textarea
                  name="to"
                  value={formData.to}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Enter client details (name, address, tax ID, etc.)&#10;Each line will be rendered as shown"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>

              <h3>Items</h3>
              <div className="invoice-items">
                {formData.items.map((item, index) => (
                  <div key={index} className="invoice-item">
                    <input type="date" value={item.date} onChange={(e) => updateItem(index, 'date', e.target.value)} />
                    <input placeholder="Description" value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} required />
                    <input type="number" placeholder="Amount" value={item.amount} onChange={(e) => updateItem(index, 'amount', e.target.value)} required />
                    <button type="button" onClick={() => removeItem(index)} className="btn-icon btn-delete">×</button>
                  </div>
                ))}
                <button type="button" onClick={addItem} className="btn btn-secondary">+ Add Item</button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tax</label>
                  <input type="number" name="tax" value={formData.tax} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <select name="currency" value={formData.currency} onChange={handleChange}>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="PKR">PKR</option>
                  </select>
                </div>
              </div>

              <div className="invoice-item-total">
                <span>Total:</span>
                <span>{formData.currency} {calculateTotal()}</span>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleChange}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <h3>Bank Details</h3>
              <div className="form-group">
                <label>Select Bank Account</label>
                <select value={selectedBank} onChange={(e) => handleBankSelect(e.target.value)}>
                  <option value="meezan">Meezan Bank (Pakistan)</option>
                  <option value="community">Wise USA</option>
                  <option value="elevate">Elevate USA</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Account Name</label>
                  <input
                    name="bank.accountName"
                    value={formData.bankDetails?.accountName || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Account Number</label>
                  <input
                    name="bank.accountNumber"
                    value={formData.bankDetails?.accountNumber || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name</label>
                  <input
                    name="bank.bankName"
                    value={formData.bankDetails?.bankName || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{selectedBank !== 'meezan' ? 'Routing Number' : 'IBAN'}</label>
                  <input
                    name={selectedBank !== 'meezan' ? 'bank.routingNumber' : 'bank.iban'}
                    value={selectedBank !== 'meezan' ? (formData.bankDetails?.routingNumber || '') : (formData.bankDetails?.iban || '')}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Swift Code</label>
                  <input
                    name="bank.swiftCode"
                    value={formData.bankDetails?.swiftCode || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Bank Address</label>
                  <input
                    name="bank.bankAddress"
                    value={formData.bankDetails?.bankAddress || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Beneficiary Address</label>
                  <input
                    name="bank.beneficiaryAddress"
                    value={formData.bankDetails?.beneficiaryAddress || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingInvoice ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
